import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Users, Clock, MapPin, Search, Filter, Star, Award, Video, Calendar as CalendarIcon, AlertCircle, UserPlus, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import EnhancedEnrollmentDialog from "@/components/enrollment/EnhancedEnrollmentDialog";
import BulkEnrollmentDialog from "@/components/enrollment/BulkEnrollmentDialog";

interface Course {
  id: string;
  title: string;
  description: string;
  duration_hours: number;
  max_participants: number;
  is_online: boolean;
  location: string;
  status: string;
  instructor_id: string;
  category_id: string;
  created_at: string;
  tags?: Tag[];
  special_event?: SpecialEvent;
  prerequisites?: CoursePrerequisite[];
  sessions?: CourseSession[];
  enrollment?: any;
  targeted_user_ids?: string[];
  target_departments?: string[];
  target_job_types?: string[];
  target_grade_levels?: string[];
  is_targeted_course?: boolean;
  meets_targeting?: boolean;
  executive_summary?: string;
  target_educator_description?: string;
  learning_objectives?: string[];
  learning_outcomes?: string[];
}

interface Tag {
  id: string;
  name: string;
  color: string;
  is_featured: boolean;
}

interface SpecialEvent {
  id: string;
  event_type: string;
  registration_deadline: string;
  early_bird_deadline: string;
  regular_price: number;
  early_bird_discount: number;
  special_instructions: string;
}

interface CoursePrerequisite {
  id: string;
  prerequisite_description: string;
  prerequisite_type: string;
  is_required: boolean;
  prerequisite_course_id?: string;
}

interface CourseSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location: string;
  max_participants: number;
  enrollments?: any[];
}

export default function EnhancedCourseCatalog() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [completedCourses, setCompletedCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [enrollmentFilter, setEnrollmentFilter] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [activeTab, setActiveTab] = useState('all-courses');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showEnrollmentDialog, setShowEnrollmentDialog] = useState(false);
  const [showBulkEnrollmentDialog, setShowBulkEnrollmentDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      let processedCourses: Course[] = [];
      
      if (user?.id) {
        // Use the new targeting function to get courses available to this user
        const { data: userCourses, error: coursesError } = await supabase
          .rpc('get_courses_for_user', { p_user_id: user.id });

        if (coursesError) throw coursesError;

        if (userCourses && userCourses.length > 0) {
          // Remove duplicates based on course_id
          const uniqueCourseIds = [...new Set(userCourses.map(c => c.course_id))];
          
          // Fetch complete course data including new fields
          const { data: fullCourseData, error: fullCourseError } = await supabase
            .from('courses')
            .select('*')
            .in('id', uniqueCourseIds);

          if (fullCourseError) throw fullCourseError;
          
          // Fetch additional course data
          const [tagMappings, specialEvents, prerequisites, sessions] = await Promise.all([
            supabase.from('course_tag_mappings')
              .select('course_id, course_tags(*)')
              .in('course_id', uniqueCourseIds),
            supabase.from('special_events')
              .select('*')
              .in('course_id', uniqueCourseIds),
            supabase.from('course_prerequisites')
              .select('*')
              .in('course_id', uniqueCourseIds),
            supabase.from('course_sessions')
              .select('*, enrollments!enrollments_session_id_fkey(id, status)')
              .in('course_id', uniqueCourseIds)
          ]);

          // Process courses data with full course information
          processedCourses = (fullCourseData || []).map(fullCourse => {
            const userCourse = userCourses.find(uc => uc.course_id === fullCourse.id);
            return {
              ...fullCourse,
              meets_targeting: userCourse?.meets_targeting || false,
              tags: tagMappings.data?.filter(tm => tm.course_id === fullCourse.id)
                               .map(tm => tm.course_tags) || [],
              special_event: specialEvents.data?.find(se => se.course_id === fullCourse.id) || null,
              prerequisites: prerequisites.data?.filter(p => p.course_id === fullCourse.id) || [],
              sessions: sessions.data?.filter(s => s.course_id === fullCourse.id)
                                .map(s => ({
                                  ...s,
                                  enrollments: s.enrollments || []
                                })) || [],
              enrollment: null
            };
          });
        }
      } else {
        // For anonymous users, get all published non-targeted courses
        const { data: anonymousCourses, error: coursesError } = await supabase
          .from('courses')
          .select('*')
          .eq('status', 'published')
          .or('is_targeted_course.is.null,is_targeted_course.eq.false');

        if (coursesError) throw coursesError;
        processedCourses = anonymousCourses?.map(course => ({
          ...course,
          tags: [],
          special_event: null,
          prerequisites: [],
          sessions: [],
          enrollment: null
        })) || [];
      }

      // Sort all courses alphabetically by title before setting state
      const sortedCourses = processedCourses.sort((a, b) => 
        a.title.localeCompare(b.title)
      );
      setCourses(sortedCourses);

      // Fetch all tags
      const { data: allTags, error: tagsError } = await supabase
        .from('course_tags')
        .select('*')
        .order('is_featured', { ascending: false });

      if (tagsError) throw tagsError;
      setTags(allTags || []);

      // Fetch user enrollments if user is logged in
      if (user?.id) {
        const { data: userEnrollments, error: enrollmentsError } = await supabase
          .from('enrollments')
          .select('*')
          .eq('participant_id', user.id);

        if (enrollmentsError) throw enrollmentsError;
        
        const userEnrollmentsData = userEnrollments || [];
        setEnrollments(userEnrollmentsData);

        // Update courses with enrollment status
        const coursesWithEnrollments = processedCourses.map(course => ({
          ...course,
          enrollment: userEnrollmentsData.find(e => e.course_id === course.id) || null
        }));
        
        // Sort courses alphabetically by title
        const sortedCourses = coursesWithEnrollments.sort((a, b) => 
          a.title.localeCompare(b.title)
        );
        setCourses(sortedCourses);

        // Filter enrolled and completed courses and sort them alphabetically
        const enrolled = sortedCourses.filter(course => 
          course.enrollment && course.enrollment.status === 'enrolled'
        ).sort((a, b) => a.title.localeCompare(b.title));
        
        const completed = sortedCourses.filter(course => 
          course.enrollment && course.enrollment.status === 'completed'
        ).sort((a, b) => a.title.localeCompare(b.title));
        
        setEnrolledCourses(enrolled);
        setCompletedCourses(completed);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load course catalog');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setSelectedCourse(course);
      setShowEnrollmentDialog(true);
    }
  };

  const handleJoinWaitlist = async (courseId: string) => {
    try {
      const { data: waitlistData } = await supabase
        .from('course_waitlists')
        .select('position')
        .eq('course_id', courseId)
        .order('position', { ascending: false })
        .limit(1);

      const nextPosition = (waitlistData?.[0]?.position || 0) + 1;

      const { error } = await supabase
        .from('course_waitlists')
        .insert({
          course_id: courseId,
          user_id: user?.id,
          position: nextPosition,
          status: 'waiting'
        });

      if (error) throw error;
      toast.success(`Added to waitlist at position ${nextPosition}`);
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast.error('Failed to join waitlist');
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTags = selectedTags.length === 0 || 
                       selectedTags.some(tagName => 
                         course.tags?.some(tag => tag.name === tagName)
                       );

    const matchesEnrollment = () => {
      switch (enrollmentFilter) {
        case 'enrolled':
          return course.enrollment && course.enrollment.status === 'enrolled';
        case 'completed':
          return course.enrollment && course.enrollment.status === 'completed';
        case 'not-enrolled':
          return !course.enrollment;
        default:
          return true;
      }
    };

    return matchesSearch && matchesTags && matchesEnrollment();
  }).sort((a, b) => a.title.localeCompare(b.title));

  const specialEventCourses = filteredCourses.filter(course => course.special_event)
    .sort((a, b) => a.title.localeCompare(b.title));
  const regularCourses = filteredCourses.filter(course => !course.special_event)
    .sort((a, b) => a.title.localeCompare(b.title));

  const toggleTag = (tagName: string) => {
    setSelectedTags(prev => 
      prev.includes(tagName) 
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  const getEventTypeBadge = (eventType: string) => {
    const colors = {
      workshop: 'bg-blue-100 text-blue-800',
      conference: 'bg-purple-100 text-purple-800',
      seminar: 'bg-green-100 text-green-800',
      training: 'bg-orange-100 text-orange-800',
      webinar: 'bg-indigo-100 text-indigo-800',
      symposium: 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge className={colors[eventType as keyof typeof colors] || 'bg-gray-100 text-gray-800'}>
        {eventType.toUpperCase()}
      </Badge>
    );
  };

  const CourseCard = ({ course }: { course: Course }) => {
    const isEnrolled = course.enrollment && course.enrollment.status === 'enrolled';
    const isCompleted = course.enrollment && course.enrollment.status === 'completed';
    const hasMultipleSessions = course.sessions && course.sessions.length > 1;
    const hasPrerequisites = course.prerequisites && course.prerequisites.length > 0;
    const isTargeted = course.is_targeted_course;
    
    return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
              {course.title}
              {course.special_event && <Star className="h-4 w-4 text-yellow-500" />}
              {course.is_online && <Video className="h-4 w-4 text-blue-500" />}
              {hasMultipleSessions && <Badge variant="secondary" className="text-xs">Multiple Sessions</Badge>}
              {hasPrerequisites && <Badge variant="outline" className="text-xs border-orange-500 text-orange-600">Prerequisites</Badge>}
              {isTargeted && <Badge className="bg-purple-100 text-purple-700 text-xs">Targeted</Badge>}
              {isEnrolled && <Badge className="bg-primary/10 text-primary">Enrolled</Badge>}
              {isCompleted && <Badge className="bg-success/10 text-success">Completed</Badge>}
            </CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {course.description}
            </CardDescription>
          </div>
          {course.special_event && getEventTypeBadge(course.special_event.event_type)}
        </div>
        
        {course.tags && course.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {course.tags.map((tag) => (
              <Badge 
                key={tag.id} 
                variant="outline" 
                style={{ borderColor: tag.color, color: tag.color }}
                className="text-xs"
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3">
          <div className="flex gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {course.duration_hours}h
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              Max {course.max_participants}
            </div>
            {!course.is_online && course.location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {course.location}
              </div>
            )}
          </div>

          {/* Multiple Sessions Info */}
          {hasMultipleSessions && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800">
                <div className="flex items-center gap-1 font-medium">
                  <Calendar className="h-4 w-4" />
                  {course.sessions!.length} session times available
                </div>
                <div className="mt-1 text-xs text-blue-600">
                  Choose your preferred time when enrolling
                </div>
              </div>
            </div>
          )}

          {hasPrerequisites && (
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <div className="text-sm">
                <div className="flex items-center gap-1 text-orange-800 font-medium">
                  <AlertCircle className="h-4 w-4" />
                  Prerequisites Required
                </div>
                <div className="mt-1 space-y-1">
                  {course.prerequisites!.slice(0, 2).map((prereq) => (
                    <div key={prereq.id} className="text-xs text-orange-700">
                      • {prereq.prerequisite_description}
                    </div>
                  ))}
                  {course.prerequisites!.length > 2 && (
                    <div className="text-xs text-orange-600">
                      +{course.prerequisites!.length - 2} more prerequisites
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {course.special_event && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg border border-yellow-200">
              <div className="text-sm space-y-1">
                {course.special_event.regular_price > 0 && (
                  <div className="font-medium">
                    Price: ${course.special_event.regular_price}
                    {course.special_event.early_bird_discount > 0 && (
                      <span className="text-green-600 ml-2">
                        (Early bird: ${course.special_event.regular_price - course.special_event.early_bird_discount})
                      </span>
                    )}
                  </div>
                )}
                {course.special_event.registration_deadline && (
                  <div className="text-orange-600">
                    Registration deadline: {new Date(course.special_event.registration_deadline).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              {!isEnrolled && !isCompleted && (
                <>
                  <Button 
                    onClick={() => handleEnroll(course.id)}
                    className="flex-1"
                    size="sm"
                  >
                    Enroll Now
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleJoinWaitlist(course.id)}
                    className="flex-1"
                    size="sm"
                  >
                    Join Waitlist
                  </Button>
                </>
              )}
              {isEnrolled && (
                <Button 
                  className="flex-1 bg-gradient-primary" 
                  size="sm"
                  onClick={() => navigate(`/course-player/${course.id}`)}
                >
                  Access Course
                </Button>
              )}
              {isCompleted && (
                <Button variant="outline" className="flex-1" size="sm">
                  View Certificate
                </Button>
              )}
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  View Details
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl">{course.title}</DialogTitle>
                </DialogHeader>
                
                {/* Full-width description */}
                <div className="text-base leading-relaxed text-muted-foreground mb-6 px-1">
                  {course.description}
                </div>

                {/* Executive Summary */}
                {course.executive_summary && (
                  <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <h4 className="font-semibold text-lg mb-2">Executive Summary</h4>
                    <p className="text-muted-foreground leading-relaxed">{course.executive_summary}</p>
                  </div>
                )}

                {/* Target Educator */}
                {course.target_educator_description && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h4 className="font-semibold text-lg mb-2 flex items-center gap-2">
                      <Target className="h-5 w-5 text-blue-600" />
                      Who Should Take This Course
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">{course.target_educator_description}</p>
                  </div>
                )}

                {/* Learning Objectives */}
                {course.learning_objectives && course.learning_objectives.length > 0 && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5 text-green-600" />
                      Learning Objectives
                    </h4>
                    <ul className="space-y-2">
                      {course.learning_objectives.map((objective, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-muted-foreground">{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Learning Outcomes */}
                {course.learning_outcomes && course.learning_outcomes.length > 0 && (
                  <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Star className="h-5 w-5 text-purple-600" />
                      Expected Outcomes
                    </h4>
                    <ul className="space-y-2">
                      {course.learning_outcomes.map((outcome, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-muted-foreground">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Course Info Column */}
                  <div className="space-y-4">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-3">Course Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{course.duration_hours} hours</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>Max {course.max_participants} participants</span>
                        </div>
                        {!course.is_online && course.location && (
                          <div className="flex items-center gap-2 col-span-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{course.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {hasPrerequisites && (
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-800">
                          <AlertCircle className="h-4 w-4" />
                          Prerequisites
                        </h4>
                        <ul className="space-y-2">
                          {course.prerequisites!.map((prereq) => (
                            <li key={prereq.id} className="text-sm flex items-start gap-2">
                              <div className="w-1.5 h-1.5 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                              <div className="flex-1">
                                {prereq.prerequisite_description}
                                {prereq.is_required && <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Sessions Column */}
                  {course.sessions && course.sessions.length > 0 && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-800">
                          <Calendar className="h-4 w-4" />
                          Available Sessions ({course.sessions.length})
                        </h4>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {course.sessions.map((session) => {
                            const enrollmentCount = session.enrollments?.length || 0;
                            const spotsLeft = (session.max_participants || course.max_participants || 0) - enrollmentCount;
                            return (
                              <div key={session.id} className="bg-white p-3 border rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="space-y-1">
                                    <div className="font-medium">
                                      {new Date(session.session_date).toLocaleDateString()}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {session.start_time} - {session.end_time}
                                    </div>
                                    {session.location && (
                                      <div className="text-sm text-muted-foreground">
                                        📍 {session.location}
                                      </div>
                                    )}
                                  </div>
                                  <Badge variant={spotsLeft > 0 ? "outline" : "destructive"}>
                                    {spotsLeft > 0 ? `${spotsLeft} left` : "Full"}
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setSelectedCourse(course);
                  setShowBulkEnrollmentDialog(true);
                }}
                className="gap-1"
              >
                <UserPlus className="h-3 w-3" />
                Bulk Enroll
              </Button>
            )}
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.location.href = `/course-management/${course.id}`}
                className="gap-1"
              >
                <Users className="h-3 w-3" />
                Manage Course
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    )
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading course catalog...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
          Professional Development Catalog
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Discover engaging courses, special events, and learning opportunities designed for educators
        </p>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={enrollmentFilter} onValueChange={setEnrollmentFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Enrollment Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              <SelectItem value="not-enrolled">Available to Enroll</SelectItem>
              <SelectItem value="enrolled">Currently Enrolled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Advanced Filters
          </Button>
        </div>

        {showAdvancedFilters && (
          <Card className="p-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-2">Filter by Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Button
                      key={tag.id}
                      variant={selectedTags.includes(tag.name) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleTag(tag.name)}
                      style={{
                        borderColor: tag.color,
                        color: selectedTags.includes(tag.name) ? 'white' : tag.color,
                        backgroundColor: selectedTags.includes(tag.name) ? tag.color : 'transparent'
                      }}
                    >
                      {tag.name}
                      {tag.is_featured && <Star className="h-3 w-3 ml-1" />}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all-courses">All Courses ({filteredCourses.length})</TabsTrigger>
          <TabsTrigger value="special-events">Special Events ({specialEventCourses.length})</TabsTrigger>
          <TabsTrigger value="my-enrollments">My Enrollments ({enrolledCourses.length + completedCourses.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all-courses" className="space-y-6">
          {specialEventCourses.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Star className="h-6 w-6 text-yellow-500" />
                Featured Special Events
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {specialEventCourses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-2xl font-semibold mb-4">Regular Courses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="special-events" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialEventCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="my-enrollments" className="space-y-6">
          {enrolledCourses.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">Currently Enrolled</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {enrolledCourses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
            </div>
          )}

          {completedCourses.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Award className="h-6 w-6 text-green-500" />
                Completed Courses
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedCourses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
            </div>
          )}

          {enrolledCourses.length === 0 && completedCourses.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">You haven't enrolled in any courses yet.</p>
              <Button onClick={() => setActiveTab('all-courses')} className="mt-4">
                Browse Available Courses
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Enhanced Enrollment Dialog */}
      {selectedCourse && (
        <EnhancedEnrollmentDialog
          open={showEnrollmentDialog}
          onOpenChange={setShowEnrollmentDialog}
          course={selectedCourse}
          onEnrollmentComplete={() => {
            fetchData(); // Refresh data after enrollment
          }}
        />
      )}

      {/* Bulk Enrollment Dialog for Admins */}
      {selectedCourse && isAdmin && (
        <BulkEnrollmentDialog
          open={showBulkEnrollmentDialog}
          onOpenChange={setShowBulkEnrollmentDialog}
          courseId={selectedCourse.id}
          courseTitle={selectedCourse.title}
          sessions={selectedCourse.sessions || []}
        />
      )}
    </div>
  );
}